import { CalendarCheck2, CalendarX2, Pencil, RotateCcw, Undo2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexAlertTiles } from '../../../../components/index-page/IndexAlertTiles';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { CancelPressConferenceModal } from '../components/CancelPressConferenceModal';
import { PressConferenceModal } from '../components/PressConferenceModal';
import { PressConferenceYearCalendar } from '../components/PressConferenceYearCalendar';
import { pressConferencesApi } from '../pressConferenceApi';
import {
  CONFERENCE_STATUS_DOT,
  CONFERENCE_STATUS_FACET,
  PRESS_CONFERENCE_AUDIT_TYPES,
  PRESS_CONFERENCE_STATUS,
  markHeldBlock,
  reopenBlock,
  pressConferenceLock,
  pressConferencePath,
  type PressConferenceItem,
} from '../pressConferenceTypes';
import { timeRange } from '../utils/timeRange';

/** Every state as a tile, each with its calendar DOT — the tiles are the year view's legend too. */
const MONITORED = [
  { value: CONFERENCE_STATUS_FACET.scheduled, status: PRESS_CONFERENCE_STATUS.scheduled },
  { value: CONFERENCE_STATUS_FACET.held, status: PRESS_CONFERENCE_STATUS.held },
  { value: CONFERENCE_STATUS_FACET.cancelled, status: PRESS_CONFERENCE_STATUS.cancelled },
];

type Pending = { kind: 'delete' | 'restore' | 'hold' | 'reopen'; record: PressConferenceItem };

/**
 * Public Relations › Press Con — the schedule of press conferences.
 *
 * **Laid out as Building Management's register is** (product call, 13 Oct 2026): a tile per state
 * over the list, each the server's count on the `conferenceStatus` facet and each narrowing the
 * list in one press; and **CALENDAR is the default view** — the year as twelve month columns,
 * through the shell's `extraView`. Table and List stay one click away in the cog.
 *
 * **Mark held and Cancel are the row's own entries**, each on its own key; either locks the
 * conference, so every write states the lock first rather than offering a click the server refuses.
 *
 * **No Clear data** — the module declares none.
 */
export function PressConferencesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PressConferenceItem | null>(null);
  const [cancelling, setCancelling] = useState<PressConferenceItem | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      pressConferencesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          conferenceStatus: query.facets.conferenceStatus?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    Edit fetches the DETAIL first — the list row does not carry the speakers, and the save REPLACES
    them, so a form opened over the row would submit an empty set and remove every speaker.
  */
  const openEdit = useCallback(
    async (record: PressConferenceItem) => {
      try {
        setEditing(await pressConferencesApi.get(record.id));
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : `Could not open ${record.referenceNumber}.`);
      }
    },
    [toast],
  );

  const rowActions = useCallback(
    (record: PressConferenceItem): MenuItem[] => [
      {
        key: `hold-${record.id}`,
        label: 'Mark held',
        icon: <CalendarCheck2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-conferences.mark-held',
        disabledReason: markHeldBlock(record, todayCalendarDate()),
        onClick: () => setPending({ kind: 'hold', record }),
      },
      {
        key: `reopen-${record.id}`,
        label: 'Reopen',
        icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-conferences.reopen',
        disabledReason: reopenBlock(record),
        onClick: () => setPending({ kind: 'reopen', record }),
      },
      {
        key: `cancel-${record.id}`,
        label: 'Cancel',
        icon: <CalendarX2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-conferences.cancel',
        disabledReason: pressConferenceLock(record),
        onClick: () => setCancelling(record),
      },
      /*
        Edit is a row ENTRY rather than `onEdit` — the shell's `onEdit` takes no disabled reason, and
        the server refuses changing a held, cancelled or deleted conference: the guard owes its sentence.
      */
      {
        key: `edit-${record.id}`,
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-conferences.update',
        disabledReason: pressConferenceLock(record),
        onClick: () => void openEdit(record),
      },
      {
        key: `restore-${record.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-conferences.delete',
        disabledReason: record.isActive ? 'This press conference is not deleted.' : null,
        onClick: () => setPending({ kind: 'restore', record }),
      },
    ],
    [openEdit],
  );

  async function runPending() {
    if (!pending) return;

    setRunning(true);
    setError(null);

    try {
      if (pending.kind === 'delete') await pressConferencesApi.remove(pending.record.id);
      else if (pending.kind === 'restore') await pressConferencesApi.restore(pending.record.id);
      else if (pending.kind === 'reopen') await pressConferencesApi.reopen(pending.record.id);
      else await pressConferencesApi.markHeld(pending.record.id);

      toast.success(
        `${pending.kind === 'delete' ? 'Deleted' : pending.kind === 'restore' ? 'Restored' : pending.kind === 'reopen' ? 'Reopened' : 'Marked held'} ${pending.record.referenceNumber}.`,
      );
      setPending(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PressConferenceItem>
        module="press-conferences"
        moduleNoun="press conferences"
        breadcrumb={['Public Relations', 'Press Con']}
        columns={COLUMNS}
        facets={[
          { key: 'conferenceStatus', label: 'Conference status' },
          { key: 'status', label: 'Status' },
        ]}
        auditEntityTypes={PRESS_CONFERENCE_AUDIT_TYPES}
        rowKey={(record) => record.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by reference, purpose, venue or remarks"
        emptyMessage="No press conferences yet. Schedule the next one."
        aboveTable={({ counts, selected, selectFacet, loading }) => (
          <IndexAlertTiles
            tiles={MONITORED.map(({ value, status }) => {
              const facet = counts.conferenceStatus?.find((f) => f.value === value);

              return {
                value,
                label: facet?.label ?? '—',
                count: facet ? facet.count : null,
                marker: CONFERENCE_STATUS_DOT[status],
              };
            })}
            selected={selected.conferenceStatus ?? []}
            loading={loading}
            onSelect={(values) => selectFacet('conferenceStatus', values)}
            emptyReason={(tile) => `Nothing in this list is ${tile.label}.`}
          />
        )}
        defaultView="extra"
        extraView={{
          label: 'Calendar',
          render: (query) => (
            <PressConferenceYearCalendar
              filters={{
                search: query.search,
                status: query.facets.status?.join(','),
                conferenceStatus: query.facets.conferenceStatus?.join(','),
              }}
              reloadKey={reloadKey}
            />
          ),
        }}
        onCreate={() => setCreating(true)}
        createPermission="press-conferences.create"
        onRowOpen={(record) => navigate(pressConferencePath(record.id))}
        openPermission="press-conferences.read"
        rowActions={rowActions}
        onDelete={(record) => setPending({ kind: 'delete', record })}
        deletePermission="press-conferences.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(record) => (record.isActive ? null : 'This press conference is already deleted.')}
        otherInfo={(record) => record}
      />

      {creating || editing !== null ? (
        <PressConferenceModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            const created = editing === null;

            setCreating(false);
            setEditing(null);

            if (created) navigate(pressConferencePath(saved.id));
            else refresh();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelPressConferenceModal
          conferenceId={cancelling.id}
          referenceNumber={cancelling.referenceNumber}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            toast.success(`Cancelled ${cancelling.referenceNumber}.`);
            setCancelling(null);
            refresh();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={() => void runPending()}
          title={
            pending.kind === 'delete'
              ? 'Delete press conference'
              : pending.kind === 'restore'
                ? 'Restore press conference'
                : pending.kind === 'reopen'
                  ? 'Reopen press conference'
                  : 'Mark press conference held'
          }
          danger={pending.kind === 'delete'}
          // A restore is the undo — no tick and no password.
          requireConfirm={pending.kind !== 'restore'}
          requireCredential={pending.kind === 'delete'}
          loading={running}
          id={`press-conference-${pending.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending.kind === 'delete'
                  ? `${pending.record.referenceNumber} — ${pending.record.purpose} leaves the register and the calendar. Nothing is erased, and it can be restored.`
                  : pending.kind === 'restore'
                    ? `${pending.record.referenceNumber} — ${pending.record.purpose} returns to the register and the calendar.`
                    : pending.kind === 'reopen'
                      ? `${pending.record.referenceNumber} — ${pending.record.purpose} goes back to Scheduled and can be edited again.${
                          pending.record.status === PRESS_CONFERENCE_STATUS.cancelled
                            ? ' The cancellation reason leaves the record; the audit trail keeps it.'
                            : ''
                        }`
                      : `${pending.record.referenceNumber} — ${pending.record.purpose} is recorded as held. After this it cannot be edited or cancelled unless it is reopened.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PressConferenceItem>[] = [
  {
    /*
      THE ANCHOR joins the reference and the purpose on ONE line — the anchor exception, one header,
      one width. Sorted by purpose; the reference is searchable.
    */
    key: 'purpose',
    header: 'Press Con',
    width: 380,
    flex: true,
    sortable: true,
    render: (record) => <TextCell code={record.referenceNumber} value={record.purpose} clamp />,
  },
  {
    // A CALENDAR date — `formatDateOnly`, never the instant path `<DateCell>` takes.
    key: 'scheduledDate',
    header: 'Date',
    width: 130,
    sortable: true,
    summary: true,
    render: (record) => <TextCell value={formatDateOnly(record.scheduledDate)} mono />,
  },
  {
    // One value — a span of the day. Not sortable on its own: the date's sort already orders by it.
    key: 'time',
    header: 'Time',
    width: 190,
    summary: true,
    render: (record) => <TextCell value={timeRange(record)} mono />,
  },
  {
    key: 'venue',
    header: 'Venue',
    width: 220,
    sortable: true,
    summary: true,
    render: (record) => <TextCell value={record.venue} />,
  },
  {
    /*
      The KIND only — no person is named on a list row (the maintenance item's rule). The external
      organization is searchable; the person and the employee are on the profile.
    */
    key: 'requestorType',
    header: 'Requested by',
    width: 170,
    render: (record) => <TextCell value={record.requestorTypeLabel ?? null} />,
  },
  {
    key: 'speakerCount',
    header: 'Speakers',
    width: 100,
    align: 'right',
    render: (record) => <TextCell value={String(record.speakerCount)} mono numeric />,
  },
  {
    key: 'conferenceStatus',
    header: 'Conference status',
    width: 160,
    stage: true,
    render: (record) => <StatusText tone="inactive">{record.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (record) =>
      record.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
